import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Sidebar, MobileSidebar } from '@/components/layout/Sidebar'
import { SessionTimeoutWarning } from '@/components/layout/SessionTimeoutWarning'
import { TenantViewBar } from '@/components/layout/TenantViewBar'
import { useSessionTimeout } from '@/hooks/useSessionTimeout'
import { useTenantScopeSync } from '@/hooks/useTenantPath'
import { TenantLayout } from '@/components/layout/TenantLayout'
import { ModuleGate } from '@/components/ui/ModuleGate'
import { StagingOnly } from '@/components/ui/StagingOnly'
import { routeChunks } from '@/routes/routeChunks'
// Eager: only the login page — everything behind auth is route-split so the
// pre-login bundle stays small.
import { LoginPage } from '@/pages/auth/LoginPage'

const DashboardPage = lazy(routeChunks['/dashboard'])
const CollectedMtdBadge = lazy(() => import('@/components/dashboard/CollectedMtdBadge').then((m) => ({ default: m.CollectedMtdBadge })))

const AcceptInvitationPage = lazy(() => import('@/pages/auth/AcceptInvitationPage').then((m) => ({ default: m.AcceptInvitationPage })))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })))
const HumanTaskDetailPage = lazy(() => import('@/pages/collections/HumanTaskDetailPage').then((m) => ({ default: m.HumanTaskDetailPage })))
// Archived 2026-02-03: Campaign pages moved to unified Flow system
const SettingsPage = lazy(routeChunks['/settings'])
const IntegrationsPage = lazy(() => import('@/pages/settings/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })))
const UsersPage = lazy(() => import('@/pages/settings/UsersPage').then((m) => ({ default: m.UsersPage })))
const EmailSettingsPage = lazy(() => import('@/pages/settings/EmailSettingsPage').then((m) => ({ default: m.EmailSettingsPage })))
const TwilioSettingsSection = lazy(() => import('@/pages/settings/TwilioSettingsSection').then((m) => ({ default: m.TwilioSettingsSection })))
const CashFlowForecastPage = lazy(routeChunks['/forecast'])
const VoiceAgentsList = lazy(() => import('@/pages/voice-agents/VoiceAgentsList'))
const CreateAgent = lazy(() => import('@/pages/voice-agents/CreateAgent'))
const AgentDetail = lazy(() => import('@/pages/voice-agents/AgentDetail'))
const VoiceAgentAnalytics = lazy(() => import('@/pages/voice-agents/Analytics'))
const TemplateGallery = lazy(() => import('@/pages/voice-agents/TemplateGallery'))
const CustomerList = lazy(() => import('@/pages/customers/CustomerList').then((m) => ({ default: m.CustomerList })))
const CustomerHubPage = lazy(routeChunks['/customers'])
const CustomerDetailPage = lazy(() => import('@/pages/customers/CustomerDetailPage').then((m) => ({ default: m.CustomerDetailPage })))
const CustomerListsPage = lazy(routeChunks['/customers/lists'])
const CustomerListBuilderPage = lazy(() => import('@/pages/customers/CustomerListBuilderPage').then((m) => ({ default: m.CustomerListBuilderPage })))
const CustomerListDetailPage = lazy(() => import('@/pages/customers/CustomerListDetailPage').then((m) => ({ default: m.CustomerListDetailPage })))
const CustomerSearchPage = lazy(routeChunks['/customers/search'])
const FlowListPage = lazy(routeChunks['/flows'])
const FlowBuilderPage = lazy(() => import('@/pages/flows/FlowBuilderPage').then((m) => ({ default: m.FlowBuilderPage })))
const FlowDetailPage = lazy(() => import('@/pages/flows/FlowDetailPage').then((m) => ({ default: m.FlowDetailPage })))
const FlowAnalyticsPage = lazy(() => import('@/pages/flows/FlowAnalyticsPage').then((m) => ({ default: m.FlowAnalyticsPage })))
const PendingTasksPage = lazy(routeChunks['/pending-tasks'])
const ActivityLogPage = lazy(routeChunks['/activity-log'])
const DNCRulesPage = lazy(() => import('@/pages/settings/DNCRulesPage').then((m) => ({ default: m.DNCRulesPage })))
const CallAnnouncementsPage = lazy(() => import('@/pages/settings/CallAnnouncementsPage').then((m) => ({ default: m.CallAnnouncementsPage })))
const CompliancePage = lazy(() => import('@/pages/settings/CompliancePage').then((m) => ({ default: m.CompliancePage })))
const CompanySettingsPage = lazy(() => import('@/pages/settings/CompanySettingsPage').then((m) => ({ default: m.CompanySettingsPage })))
const OutreachTemplatesPage = lazy(() => import('@/pages/settings/OutreachTemplatesPage').then((m) => ({ default: m.OutreachTemplatesPage })))
const OutreachTemplateEditorPage = lazy(() => import('@/pages/settings/OutreachTemplateEditorPage').then((m) => ({ default: m.OutreachTemplateEditorPage })))
const NotifyTemplateEditorPage = lazy(() => import('@/pages/settings/NotifyTemplateEditorPage').then((m) => ({ default: m.NotifyTemplateEditorPage })))
const NotifyTemplatesPage = lazy(() => import('@/pages/settings/NotifyTemplatesPage').then((m) => ({ default: m.NotifyTemplatesPage })))
const CustomVariablesPage = lazy(() => import('@/pages/settings/CustomVariablesPage').then((m) => ({ default: m.CustomVariablesPage })))
const ManagementEmailsPage = lazy(() => import('@/pages/settings/ManagementEmailsPage').then((m) => ({ default: m.ManagementEmailsPage })))
const ReportMappingPage = lazy(() => import('@/pages/settings/ReportMappingPage').then((m) => ({ default: m.ReportMappingPage })))
const PaymentGatewayPage = lazy(() => import('@/pages/settings/PaymentGatewayPage').then((m) => ({ default: m.PaymentGatewayPage })))
const TemplatesPage = lazy(() => import('@/pages/templates/TemplatesPage'))
const ARInvoicesPage = lazy(() => import('@/pages/collections/ARInvoicesPage').then((m) => ({ default: m.ARInvoicesPage })))
const InvoiceDetailPage = lazy(() => import('@/pages/collections/InvoiceDetailPage').then((m) => ({ default: m.InvoiceDetailPage })))
const TechnicianMetricsPage = lazy(() => import('@/pages/collections/TechnicianMetricsPage').then((m) => ({ default: m.TechnicianMetricsPage })))
const PendingPaymentsPage = lazy(routeChunks['/collections/pending-payments'])
const ActionCenterPage = lazy(routeChunks['/action-center'])
const RiskDashboardPage = lazy(routeChunks['/risk'])
const PaymentAttemptsPage = lazy(routeChunks['/payment-attempts'])
const RiskConfigPage = lazy(() => import('@/pages/risk/RiskConfigPage').then((m) => ({ default: m.RiskConfigPage })))
const RecaptureDashboardPage = lazy(() => import('@/pages/recapture/RecaptureDashboardPage').then((m) => ({ default: m.RecaptureDashboardPage })))
const MembershipListPage = lazy(() => import('@/pages/membership/MembershipListPage').then((m) => ({ default: m.MembershipListPage })))
const EstimateDashboardPage = lazy(() => import('@/pages/estimates/EstimateDashboardPage').then((m) => ({ default: m.EstimateDashboardPage })))
const OrganizationsPage = lazy(routeChunks['/organizations'])
const OrganizationAdminsPage = lazy(() => import('@/pages/super-admin/OrganizationAdminsPage').then((m) => ({ default: m.OrganizationAdminsPage })))
const SuperAdminHumanTasksPage = lazy(routeChunks['/human-tasks'])
const SuperAdminHumanTaskDetailPage = lazy(() => import('@/pages/super-admin/SuperAdminHumanTaskDetailPage').then((m) => ({ default: m.SuperAdminHumanTaskDetailPage })))
const SuperAdminCustomersPage = lazy(() => import('@/pages/super-admin/SuperAdminCustomersPage').then((m) => ({ default: m.SuperAdminCustomersPage })))
const SuperAdminManagementPage = lazy(routeChunks['/super-admins'])
const PromoteSuperAdminPage = lazy(() => import('@/pages/super-admin/PromoteSuperAdminPage').then((m) => ({ default: m.PromoteSuperAdminPage })))
const SystemSettingsPage = lazy(routeChunks['/system-settings'])
const OperationalConfigPage = lazy(routeChunks['/operational-config'])
const OperationsPage = lazy(routeChunks['/operations'])
const ApprovalPage = lazy(() => import('@/pages/approvals/ApprovalPage').then((m) => ({ default: m.ApprovalPage })))
const CustomerViewPage = lazy(() => import('@/pages/customer-view/CustomerViewPage').then((m) => ({ default: m.CustomerViewPage })))
const UnopenedEmailsPage = lazy(() => import('@/pages/reports/UnopenedEmailsPage').then((m) => ({ default: m.UnopenedEmailsPage })))
const DeferredRevenuePage = lazy(routeChunks['/reports/deferred-revenue'])
const CollectedPaymentsPage = lazy(() => import('@/pages/collections/CollectedPaymentsPage').then((m) => ({ default: m.CollectedPaymentsPage })))
const BrandComparisonPage = lazy(routeChunks['/brand-comparison'])

function PageSpinner() {
  return (
    <div className="flex items-center justify-center h-full min-h-[50vh] bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  )
}

function SuperAdminGuard() {
  const user = useAuthStore((s) => s.user)
  if (user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />
  }
  return <Outlet />
}

function TenantAdminGuard() {
  const user = useAuthStore((s) => s.user)
  const activeTenantId = useAuthStore((s) => s.activeTenantId)
  const tenant = useAuthStore((s) => s.tenant)
  // Super admins viewing a tenant get full access
  if (user?.role === 'super_admin' && activeTenantId) {
    return <Outlet />
  }
  // Tenant admins get access
  if (user?.role === 'tenant_admin') {
    return <Outlet />
  }
  // Collections agents get redirected to dashboard
  const tenantId = activeTenantId || tenant?.id
  return <Navigate to={tenantId ? `/${tenantId}/dashboard` : '/dashboard'} replace />
}

function CollectionsGuard() {
  const user = useAuthStore((s) => s.user)
  const activeTenantId = useAuthStore((s) => s.activeTenantId)
  const tenant = useAuthStore((s) => s.tenant)
  if (
    user?.role === 'super_admin' ||
    user?.role === 'tenant_admin' ||
    user?.role === 'collections_agent'
  ) {
    return <Outlet />
  }
  const tenantId = activeTenantId || tenant?.id
  return <Navigate to={tenantId ? `/${tenantId}/dashboard` : '/dashboard'} replace />
}

function CustomersRoute() {
  const user = useAuthStore((s) => s.user)
  const activeTenantId = useAuthStore((s) => s.activeTenantId)
  if (user?.role === 'super_admin' && !activeTenantId) {
    return <SuperAdminCustomersPage />
  }
  return <CustomerList />
}

function ProtectedLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const hasHydrated = useAuthStore((s) => s.hasHydrated)
  const user = useAuthStore((s) => s.user)
  const tenant = useAuthStore((s) => s.tenant)
  const activeTenantId = useAuthStore((s) => s.activeTenantId)
  const logout = useAuthStore((s) => s.logout)
  const location = useLocation()
  const { isWarning, remainingSeconds, stayLoggedIn } = useSessionTimeout()

  // The one writer of the tenant scope. Lives here, above the route Suspense
  // boundary below, so a suspending lazy chunk can't tear it down and re-run it.
  useTenantScopeSync()

  // Wait for zustand to rehydrate from localStorage before checking auth
  if (!hasHydrated) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />
  }

  // Redirect tenant users to tenant-prefixed routes. Key off activeTenantId
  // (the currently-switched tenant) rather than the login-default `tenant`, so
  // "Switch Company" navigations to /<newTenant>/... aren't bounced straight
  // back to the original tenant. activeTenantId === tenant.id at login, so this
  // is a no-op for single-company users.
  const activeId = activeTenantId || tenant?.id
  if (user.role !== 'super_admin' && activeId) {
    const tenantPrefix = `/${activeId}`
    if (!location.pathname.startsWith(tenantPrefix)) {
      return <Navigate to={`${tenantPrefix}/dashboard`} replace />
    }
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header with hamburger menu */}
        <div className="flex items-center px-4 h-14 md:hidden">
          <MobileSidebar />
          <img src="/fdclogo-banner.png" alt="FrontDeskCollect.ai" className="h-6 w-auto ml-2" />
        </div>
        <TenantViewBar />
        <main className="flex-1 overflow-auto">
          <ErrorBoundary>
            {/* Keeps the sidebar/layout mounted while a lazy route chunk loads */}
            <Suspense fallback={<PageSpinner />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      {/* Own boundary: without it, this lazy chunk suspends the App-level
          Suspense and blanks the whole layout while it loads. */}
      <Suspense fallback={null}>
        <CollectedMtdBadge />
      </Suspense>
      <SessionTimeoutWarning
        isWarning={isWarning}
        remainingSeconds={remainingSeconds}
        onStayLoggedIn={stayLoggedIn}
        onLogout={logout}
      />
    </div>
  )
}

function App() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-screen bg-background">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      }
    >
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/approval" element={<ApprovalPage />} />
        <Route path="/customer-view" element={<CustomerViewPage />} />
        <Route path="/" element={<ProtectedLayout />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />

          {/* Brand comparison — super admins see all brands at the top level. */}
          <Route element={<SuperAdminGuard />}>
            <Route path="brand-comparison" element={<BrandComparisonPage />} />
          </Route>

          {/* Super Admin (no tenant context needed) */}
          <Route element={<SuperAdminGuard />}>
            <Route path="customers" element={<CustomersRoute />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="organizations/:tenantId/admins" element={<OrganizationAdminsPage />} />
            <Route path="human-tasks" element={<SuperAdminHumanTasksPage />} />
            <Route path="human-tasks/:id" element={<SuperAdminHumanTaskDetailPage />} />
            <Route path="super-admins/promote" element={<PromoteSuperAdminPage />} />
            <Route path="super-admins" element={<SuperAdminManagementPage />} />
            <Route path="system-settings" element={<SystemSettingsPage />} />
            <Route path="operational-config" element={<OperationalConfigPage />} />
            <Route path="operations" element={<OperationsPage />} />
          </Route>

          {/* Tenant-scoped routes with tenant ID in URL (for super admin tenant viewing) */}
          <Route path=":tenantId" element={<TenantLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />

            {/* Collections */}
            <Route path="collections" element={<Navigate to="../dashboard" replace />} />
            <Route path="collections/dashboard" element={<Navigate to="../dashboard" replace />} />
            <Route path="collections/queue" element={<Navigate to="../action-center" replace />} />
            <Route path="collections/human-task/:id" element={<HumanTaskDetailPage />} />
            <Route path="collections/ar-invoices" element={<ARInvoicesPage />} />
            <Route path="collections/invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="collections/technician-metrics" element={<TechnicianMetricsPage />} />
            <Route path="collections/collected-payments" element={<CollectedPaymentsPage />} />

            {/* Promises Radar – redirect to unified Pending Payments tab */}
            <Route path="promises" element={<Navigate to="/collections/pending-payments?tab=promises" replace />} />

            {/* Pending Payments — collections agents + admins only */}
            <Route element={<CollectionsGuard />}>
              <Route path="collections/pending-payments" element={<PendingPaymentsPage />} />
            </Route>

            {/* Action Center — the collector's queue screen. The old
                /actionflow and /engage paths stay as redirects for bookmarks;
                nothing in the app links to them. */}
            <Route path="action-center" element={<ActionCenterPage />} />
            <Route path="actionflow" element={<Navigate to="../action-center" replace />} />
            <Route path="engage" element={<Navigate to="../action-center" replace />} />

            {/* Membership/Retention */}
            <Route path="membership" element={<ModuleGate module="membership_expiration" fallback={<Navigate to="../customers" replace />}><MembershipListPage /></ModuleGate>} />
            <Route path="membership/:id" element={<Navigate to="../customers" replace />} />

            {/* Brand comparison — reachable by multi-brand members (nav hides it
                for single-brand users); see Sidebar gating. */}
            <Route path="brand-comparison" element={<BrandComparisonPage />} />

            {/* Templates Hub */}
            <Route path="templates" element={<TemplatesPage />} />

            {/* Customers hub */}
            <Route path="customers" element={<CustomerHubPage />} />
            <Route path="customers/search" element={<CustomerSearchPage />} />
            <Route path="customers/lists" element={<CustomerListsPage />} />
            <Route path="customers/lists/new" element={<CustomerListBuilderPage />} />
            <Route path="customers/lists/:id" element={<CustomerListDetailPage />} />
            <Route path="customers/lists/:id/edit" element={<CustomerListBuilderPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />

            {/* Reports */}
            <Route path="reports" element={<Navigate to="../dashboard" replace />} />
            <Route path="reports/recovery" element={<Navigate to="../dashboard" replace />} />
            <Route path="reports/agents" element={<Navigate to="../dashboard" replace />} />
            <Route path="reports/campaigns" element={<Navigate to="../flows" replace />} />
            <Route path="reports/unopened-emails" element={<UnopenedEmailsPage />} />
            <Route path="reports/deferred-revenue" element={<ModuleGate module="membership_expiration" fallback={<Navigate to="../dashboard" replace />}><DeferredRevenuePage /></ModuleGate>} />

            {/* Risk Scoring (read-only dashboard — all roles) */}
            <Route path="risk" element={<RiskDashboardPage />} />
            <Route path="payment-attempts" element={<StagingOnly><PaymentAttemptsPage /></StagingOnly>} />

            {/* Recapture (Missed Bookings) */}
            <Route path="recapture" element={<ModuleGate module="recapture" fallback={<Navigate to="../customers" replace />}><RecaptureDashboardPage /></ModuleGate>} />

            {/* Estimate Follow Up */}
            <Route path="estimates" element={<ModuleGate module="estimate_followup" fallback={<Navigate to="../customers" replace />}><EstimateDashboardPage /></ModuleGate>} />

            {/* Activity Log (renamed from Communications) */}
            <Route path="activity-log" element={<ActivityLogPage />} />
            <Route path="communications" element={<Navigate to="../activity-log" replace />} />

            {/* Tenant-admin-only routes */}
            <Route element={<TenantAdminGuard />}>
              {/* Settings */}
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/integrations" element={<IntegrationsPage />} />
              <Route path="settings/payment-gateway" element={<PaymentGatewayPage />} />
              <Route path="settings/report-mapping" element={<ReportMappingPage />} />
              <Route path="settings/users" element={<UsersPage />} />
              <Route path="settings/email" element={<EmailSettingsPage />} />
              <Route path="settings/sms" element={<TwilioSettingsSection />} />
              <Route path="settings/company" element={<CompanySettingsPage />} />
              <Route path="settings/dnc-rules" element={<DNCRulesPage />} />
              <Route path="settings/call-announcements" element={<CallAnnouncementsPage />} />
              <Route path="settings/compliance" element={<CompliancePage />} />
              <Route path="settings/outreach-templates" element={<OutreachTemplatesPage />} />
              <Route path="settings/custom-variables" element={<CustomVariablesPage />} />
              <Route path="settings/management-emails" element={<ManagementEmailsPage />} />
              <Route path="settings/outreach-templates/new" element={<OutreachTemplateEditorPage />} />
              <Route path="settings/outreach-templates/:id/edit" element={<OutreachTemplateEditorPage />} />
              <Route path="settings/notify-templates" element={<NotifyTemplatesPage />} />
              <Route path="settings/notify-templates/new" element={<NotifyTemplateEditorPage />} />
              <Route path="settings/notify-templates/:id/edit" element={<NotifyTemplateEditorPage />} />

              {/* Voice Agents */}
              <Route path="settings/voice-agents" element={<VoiceAgentsList />} />
              <Route path="settings/voice-agents/new" element={<CreateAgent />} />
              <Route path="settings/voice-agents/templates" element={<TemplateGallery />} />
              <Route path="settings/voice-agents/:id" element={<AgentDetail />} />
              <Route path="settings/voice-agents/:id/prompts" element={<Navigate to=".." relative="path" replace />} />
              <Route path="settings/voice-agents/:id/analytics" element={<VoiceAgentAnalytics />} />

              {/* Risk Config */}
              <Route path="settings/risk" element={<RiskConfigPage />} />

              {/* Forecast */}
              <Route path="forecast" element={<CashFlowForecastPage />} />

              {/* Flows (Automation) */}
              <Route path="flows" element={<FlowListPage />} />
              <Route path="flows/notifications" element={<ManagementEmailsPage />} />
              <Route path="flows/notifications/new" element={<ManagementEmailsPage />} />
              <Route path="flows/notifications/:id/edit" element={<ManagementEmailsPage />} />
              <Route path="flows/new" element={<FlowBuilderPage />} />
              <Route path="flows/logs" element={<Navigate to="../activity-log" replace />} />
              <Route path="flows/:id" element={<FlowDetailPage />} />
              <Route path="flows/:id/edit" element={<FlowBuilderPage />} />
              <Route path="flows/:id/analytics" element={<FlowAnalyticsPage />} />
              <Route path="flows/:id/outbox" element={<Navigate to="../../pending-tasks" replace />} />
              <Route path="pending-tasks" element={<PendingTasksPage />} />
              <Route path="outbox" element={<Navigate to="../pending-tasks" replace />} />
            </Route>

            {/* Catch-all: redirect unknown tenant sub-paths to dashboard */}
            <Route path="*" element={<Navigate to="../dashboard" replace />} />
          </Route>

          {/* Catch-all: redirect unknown top-level paths to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default App
